type NormalizedApiError = {
  status: number;
  message: string;
  code?: string;
  fieldErrors?: Record<string, string[]>;
  missingRequiredFields?: Record<string, string[]>;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly fieldErrors?: Record<string, string[]>;
  readonly missingRequiredFields?: Record<string, string[]>;

  constructor(error: NormalizedApiError) {
    super(error.message);
    this.name = "ApiError";
    this.status = error.status;
    this.code = error.code;
    this.fieldErrors = error.fieldErrors;
    this.missingRequiredFields = error.missingRequiredFields;
  }
}

function asStringArray(value: unknown): string[] | undefined {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === "string") return [value];
  return undefined;
}

export function normalizeDrfError(payload: unknown, status: number) {
  // DRF commonly returns:
  // - {"detail": "...", "code": "..."} — business errors (code is a top-level body key)
  // - {"non_field_errors": ["..."]}
  // - {"field": ["..."], "field2": ["..."]}
  if (payload && typeof payload === "object") {
    const obj = payload as Record<string, unknown>;

    const code = typeof obj.code === "string" ? obj.code : undefined;
    const missingRequiredFields =
      obj.missing_required_fields && typeof obj.missing_required_fields === "object"
        ? (obj.missing_required_fields as Record<string, unknown>)
        : undefined;
    const missingRequiredFieldsNormalized: Record<string, string[]> | undefined = missingRequiredFields
      ? Object.fromEntries(
          Object.entries(missingRequiredFields).flatMap(([k, v]) => {
            const arr = asStringArray(v);
            return arr?.length ? [[k, arr]] : [];
          }),
        )
      : undefined;

    if ("detail" in obj) {
      const detail = obj.detail;
      return {
        message: typeof detail === "string" ? detail : `Request failed (${status})`,
        code,
        fieldErrors: undefined,
        missingRequiredFields: missingRequiredFieldsNormalized,
      };
    }

    const nfe = asStringArray(obj.non_field_errors);
    if (nfe?.length) {
      return { message: nfe[0] ?? `Request failed (${status})`, code, fieldErrors: undefined, missingRequiredFields: missingRequiredFieldsNormalized };
    }

    const fieldErrors: Record<string, string[]> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (key === "detail") continue;
      if (key === "code") continue;
      if (key === "missing_required_fields") continue;
      const arr = asStringArray(value);
      if (arr?.length) fieldErrors[key] = arr;
    }

    const firstField = Object.keys(fieldErrors)[0];
    if (firstField) {
      return {
        message: fieldErrors[firstField]?.[0] ?? `Request failed (${status})`,
        code,
        fieldErrors,
        missingRequiredFields: missingRequiredFieldsNormalized,
      };
    }
  }

  return { message: `Request failed (${status})`, code: undefined, fieldErrors: undefined, missingRequiredFields: undefined };
}

// Session-missing / dead-questionnaire details the backend can return; the API
// has no machine-readable code for these yet, so display mapping (like the
// retry logic in useQuizWizardController) has to substring-match `detail`.
const SESSION_EXPIRED_SNIPPETS = [
  "anonymous session not provided",
  "anonymous session not found",
  "session cookie expired",
  "invalid session cookie",
  "questionnaire instance not found",
];

/**
 * Map a normalized API error message to user-facing copy for the known
 * technical failure modes (bot check, throttling, dead session, opaque 5xx).
 * Unrecognized messages pass through unchanged — field-level validation
 * errors are already human-readable and must not be rewritten.
 */
export function humanizeApiMessage(message: string, status?: number, code?: string): string {
  const text = message.toLowerCase();

  // The enquiry idempotency-conflict retry (EnquireModal.tsx) already
  // handles the common case transparently; this only surfaces if the retry
  // itself also conflicts (e.g. a double-submit race), so it's a rare
  // defense-in-depth fallback rather than the primary UX for the conflict.
  // Kept surface-neutral (not "enquiry details") — questionnaire_service.py
  // emits this same code from the step PATCH and /complete, so a double-submit
  // race on the quiz goes through this exact branch too, not just enquiries.
  if (code === "idempotency_conflict") {
    return "It looks like your details changed since your last attempt. Please review them and try again.";
  }
  // The quiz controller handles a login_required 403 as its own
  // "auth_required" init state on the first occurrence per mount. This branch
  // is reachable on a *second* one in the same mount (the gate-shown cap in
  // useQuizWizardController falls through to the generic error path rather
  // than reopening the gate), plus it guards against the raw detail leaking
  // as a headline if the 403 ever surfaces somewhere else.
  if (code === "login_required") {
    return "Please log in or create an account to take the quiz again.";
  }
  if (text.includes("bot check failed")) {
    return "We couldn't verify your browser. Please try again. Your answers are safe.";
  }
  if (status === 429 || text.includes("throttled")) {
    return "Too many attempts. Please wait a moment and try again.";
  }
  if (SESSION_EXPIRED_SNIPPETS.some((s) => text.includes(s))) {
    return "Your session expired. Please retry.";
  }
  // normalizeDrfError's fallback for opaque bodies (5xx, HTML error pages).
  if (/^request failed \(\d+\)$/.test(text)) {
    return "Something went wrong on our side. Please try again.";
  }
  return message;
}
